/**
 * 参数面板（lil-gui）：参数、预设、渲染/教学开关。
 * 移动端容器在 styles.css 中做成抽屉。
 */

import { GUI } from 'lil-gui'
import type { SceneManager } from '../core/SceneManager'
import type { QualityTier } from '../core/Quality'
import type { ScatteringMode } from '../atmosphere/types'

export class Panel {
  private gui: GUI
  private controllers: Record<string, { updateDisplay: () => void }> = {}

  constructor(private sm: SceneManager, container: HTMLElement) {
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches

    this.gui = new GUI({
      container,
      title: '☀️ 大气参数',
      width: isMobile ? 300 : 280,
    })
    this.build()
    this.sm.onParamsChanged = () => this.refresh()
  }

  private build(): void {
    const p = this.sm.params

    // ---- 预设 ----
    const presetFolder = this.gui.addFolder('预设场景')
    presetFolder
      .add(
        {
          白天: () => this.sm.applyPreset('白天'),
          黄昏: () => this.sm.applyPreset('黄昏'),
          夜晚: () => this.sm.applyPreset('夜晚'),
          太空: () => this.sm.applyPreset('太空'),
        },
        '白天',
      )
      .name('▶ 白天')
    presetFolder
      .add(
        {
          黄昏: () => this.sm.applyPreset('黄昏'),
        },
        '黄昏',
      )
      .name('▶ 黄昏')
    presetFolder
      .add(
        {
          夜晚: () => this.sm.applyPreset('夜晚'),
        },
        '夜晚',
      )
      .name('▶ 夜晚')
    presetFolder
      .add(
        {
          太空: () => this.sm.applyPreset('太空'),
        },
        '太空',
      )
      .name('▶ 太空')

    // ---- 太阳 ----
    const sun = this.gui.addFolder('太阳')
    this.controllers.sunElevationDeg = sun
      .add(p, 'sunElevationDeg', -10, 90, 0.1)
      .name('仰角 (°)')
      .onChange(() => this.sm.applyParams())
    this.controllers.sunAzimuthDeg = sun
      .add(p, 'sunAzimuthDeg', 0, 360, 1)
      .name('方位角 (°)')
      .onChange(() => this.sm.applyParams())

    // ---- 大气 ----
    const atmo = this.gui.addFolder('大气')
    this.controllers.turbidity = atmo
      .add(p, 'turbidity', 1, 10, 0.05)
      .name('浊度 Turbidity')
      .onChange(() => this.sm.applyParams())
    this.controllers.aerosolDensity = atmo
      .add(p, 'aerosolDensity', 0, 1, 0.01)
      .name('气溶胶密度')
      .onChange(() => this.sm.applyParams())

    // ---- 相机 ----
    const cam = this.gui.addFolder('相机')
    this.controllers.altitudeKm = cam
      .add(p, 'altitudeKm', 0.002, 600, 0.001)
      .name('海拔 (km)')
      .onChange(() => this.sm.applyParams())

    // ---- 渲染 ----
    const render = this.gui.addFolder('渲染')
    this.controllers.quality = render
      .add(p, 'quality', ['high', 'medium', 'low'])
      .name('质量档')
      .onChange((v: QualityTier) => this.sm.setQuality(v))
    this.controllers.scatteringMode = render
      .add(p, 'scatteringMode', ['single', 'secondOrder', 'precomputedLUT'])
      .name('散射模式')
      .onChange((v: ScatteringMode) => {
        p.scatteringMode = v
        this.sm.applyParams()
      })
    this.controllers.rayleighOnly = render
      .add(p, 'rayleighOnly')
      .name('仅 Rayleigh')
      .onChange(() => this.sm.applyParams())
    this.controllers.mieOnly = render
      .add(p, 'mieOnly')
      .name('仅 Mie')
      .onChange(() => this.sm.applyParams())
    this.controllers.noScatter = render
      .add(p, 'noScatter')
      .name('无散射 baseline')
      .onChange(() => this.sm.applyParams())

    // ---- 曝光 ----
    const exp = this.gui.addFolder('曝光')
    this.controllers.manualExposure = exp
      .add(p, 'manualExposure', 0.1, 10, 0.05)
      .name('手动曝光')
      .onChange(() => this.sm.applyParams())
    this.controllers.adaptSpeed = exp
      .add(p, 'adaptSpeed', 0.1, 5, 0.05)
      .name('自适应速度')
      .onChange(() => this.sm.applyParams())

    // ---- 多次散射 ----
    const ms = this.gui.addFolder('多次散射')
    this.controllers.msIntensity = ms
      .add(p, 'msIntensity', 0, 1, 0.01)
      .name('强度')
      .onChange(() => this.sm.applyParams())
    this.controllers.msDirCount = ms
      .add(p, 'msDirCount', 4, 16, 2)
      .name('二次采样数')
      .onChange(() => this.sm.applyParams())

    presetFolder.open()
    sun.open()
    atmo.open()
    render.open()
  }

  /** 同步显示（参数被外部改动后调用） */
  refresh(): void {
    for (const key of Object.keys(this.controllers)) {
      this.controllers[key].updateDisplay()
    }
  }
}
